import { v4 as uuidv4 } from "uuid";

// Every browser recorder on this origin shares one microphone owner. The lock
// lasts until release or document termination; a backgrounded/frozen window
// must never lose ownership merely because a heartbeat stopped running.
export const WEB_RECORDING_LOCK_NAME = "lucasai:web-recording";
export const WEB_RECORDING_JOURNAL_KEY = "webActiveRecording";

const STATES = new Set(["starting", "recording", "finalizing", "interrupted"]);
const validId = (value) =>
  (typeof value === "string" && value.trim().length > 0) || (typeof value === "number" && Number.isFinite(value));
const sameIdentity = (left, right) => left?.recordingUuid === right?.recordingUuid && left?.ownerToken === right?.ownerToken;
const sameAccount = (left, right) => left?.userId === right?.userId && (left?.orgId ?? null) === (right?.orgId ?? null);

export function readWebRecordingJournal() {
  try {
    const raw = localStorage.getItem(WEB_RECORDING_JOURNAL_KEY);
    if (raw === null) return { status: "ok", journal: null };
    const journal = JSON.parse(raw);
    if (
      journal?.version !== 1 ||
      typeof journal.recordingUuid !== "string" ||
      !journal.recordingUuid ||
      typeof journal.ownerToken !== "string" ||
      !journal.ownerToken ||
      !validId(journal.userId) ||
      !STATES.has(journal.state) ||
      !Number.isFinite(journal.startedAt) ||
      !Number.isFinite(journal.updatedAt)
    )
      return { status: "storage-error" };
    return { status: "ok", journal };
  } catch (_) {
    // A failed read is not proof that no recording exists. Retain corrupt data
    // for diagnosis instead of replacing an unresolved recording's journal.
    return { status: "storage-error" };
  }
}

// A stale heartbeat cannot authorize recovery while the recorder's document
// still holds its Web Lock. null means inspection failed, so callers must keep
// the audio queued. Journals from this protocol are required before capture;
// a different/missing journal therefore leaves legacy recordings to their
// existing recovery rules without requiring Web Locks on older browsers.
export async function hasLiveWebRecordingOwner(recordingUuid) {
  if (typeof recordingUuid !== "string" || !recordingUuid) return null;
  const stored = readWebRecordingJournal();
  if (stored.status !== "ok") return null;
  if (stored.journal?.recordingUuid !== recordingUuid) return false;
  try {
    const lockManager = typeof navigator !== "undefined" ? navigator.locks : undefined;
    if (typeof lockManager?.query !== "function") return null;
    const snapshot = await lockManager.query();
    if (!Array.isArray(snapshot?.held)) return null;
    return snapshot.held.some((lock) => lock.name === WEB_RECORDING_LOCK_NAME);
  } catch (_) {
    return null;
  }
}

function writeJournal(journal) {
  localStorage.setItem(WEB_RECORDING_JOURNAL_KEY, JSON.stringify(journal));
  const stored = readWebRecordingJournal();
  return stored.status === "ok" && sameIdentity(stored.journal, journal) && stored.journal.state === journal.state;
}

/**
 * Atomically claim browser recording ownership before asking for a microphone.
 * recoverPending runs under the exclusive lock and must return literal true
 * only after proving complete audio custody or an explicit, account-scoped user
 * acknowledgment of the interruption that preserves any remaining saved audio.
 * A missing/failed review leaves the previous journal intact and blocks start.
 */
export async function acquireWebRecordingOwnership(metadata, { recoverPending } = {}) {
  const lockManager = typeof navigator !== "undefined" ? navigator.locks : undefined;
  if (!lockManager?.request) return { status: "unsupported" };
  if (typeof metadata?.recordingUuid !== "string" || !metadata.recordingUuid || !validId(metadata?.userId))
    return { status: "invalid-identity" };

  return new Promise((resolve) => {
    let requestTask;
    try {
      requestTask = Promise.resolve(
        lockManager.request(WEB_RECORDING_LOCK_NAME, { mode: "exclusive", ifAvailable: true }, async (lock) => {
          if (!lock) {
            resolve({ status: "busy" });
            return;
          }
          const previous = readWebRecordingJournal();
          if (previous.status !== "ok") {
            resolve(previous);
            return;
          }
          if (previous.journal) {
            let recovered = false;
            if (sameAccount(previous.journal, metadata) && typeof recoverPending === "function") {
              try {
                recovered = (await recoverPending({ ...previous.journal })) === true;
              } catch (_) {}
            }
            if (!recovered) {
              resolve({ status: "pending", journal: previous.journal });
              return;
            }
            // The recovery can await IndexedDB/network work. Recheck the
            // journal before replacing it, even though cooperating windows
            // cannot change ownership while this lock is held.
            const current = readWebRecordingJournal();
            if (current.status !== "ok" || !sameIdentity(current.journal, previous.journal)) {
              resolve(current.status === "ok" ? { status: "pending", journal: current.journal } : current);
              return;
            }
          }

          let journal = {
            version: 1,
            ownerToken: uuidv4(),
            recordingUuid: metadata.recordingUuid,
            userId: metadata.userId,
            orgId: metadata.orgId ?? null,
            chatId: metadata.chatId ?? null,
            phase: metadata.phase ?? null,
            startedAt: Number.isFinite(metadata.startedAt) ? metadata.startedAt : Date.now(),
            updatedAt: Date.now(),
            state: "starting",
          };
          try {
            if (!writeJournal(journal)) throw new Error("Recording journal could not be verified");
          } catch (_) {
            resolve({ status: "storage-error" });
            return;
          }

          let released = false;
          let releaseLock;
          const held = new Promise((resolveHeld) => {
            releaseLock = resolveHeld;
          });
          const update = (state) => {
            if (released || !STATES.has(state)) return false;
            const current = readWebRecordingJournal();
            if (current.status !== "ok" || !sameIdentity(current.journal, journal)) return false;
            const next = { ...journal, state, updatedAt: Date.now() };
            try {
              if (!writeJournal(next)) return false;
              journal = next;
              return true;
            } catch (_) {
              return false;
            }
          };
          const handle = {
            get journal() {
              return { ...journal };
            },
            update,
            async release({ clearJournal = false } = {}) {
              if (released) {
                await requestTask;
                return false;
              }
              let changed = false;
              if (clearJournal) {
                const current = readWebRecordingJournal();
                if (current.status === "ok" && sameIdentity(current.journal, journal)) {
                  try {
                    localStorage.removeItem(WEB_RECORDING_JOURNAL_KEY);
                    changed = localStorage.getItem(WEB_RECORDING_JOURNAL_KEY) === null;
                  } catch (_) {}
                }
              } else {
                changed = update("interrupted");
              }
              released = true;
              releaseLock();
              await requestTask;
              return changed;
            },
          };
          resolve({ status: "acquired", handle });
          await held;
        })
      );
      requestTask.catch(() => resolve({ status: "storage-error" }));
    } catch (_) {
      resolve({ status: "storage-error" });
    }
  });
}
